import { Folder, Info, Link, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/i18n";

/**
 * Props for the MetadataBadge component.
 */
interface MetadataBadgeProps {
  /** Display name of the item */
  readonly name: string;
  /** Optional description shown in tooltip */
  readonly description?: string;
  /** Whether this item is linked to an existing item */
  readonly isLinked?: boolean;
  /** Whether this item has a parent set */
  readonly hasParent?: boolean;
  /** Optional parent name to show in tooltip */
  readonly parentName?: string;
  /** Original extracted name from document (shown when linked) */
  readonly extractedName?: string;
  /** Callback when edit is clicked */
  readonly onEdit?: () => void;
}

/**
 * MetadataBadge component displays a single metadata item as a badge.
 * Shows an info icon with tooltip if description is provided.
 * Shows a folder icon if item has a parent set.
 * Shows an edit icon on hover that opens edit modal when clicked.
 *
 * @param props - Component props
 * @returns Badge element with optional tooltip and edit button
 */
export default function MetadataBadge({
  name,
  description,
  isLinked = false,
  hasParent = false,
  parentName,
  extractedName,
  onEdit,
}: MetadataBadgeProps) {
  const hasDescription = Boolean(description);
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useTranslation();

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#ffffff] border border-[#e4e4e7] text-sm text-[#3f3f46] font-semibold rounded-lg relative group cursor-pointer hover:border-[#d4d4d8] transition-colors"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={onEdit}
    >
      {isLinked && <Link className="w-3 h-3 text-[#71717a] shrink-0" />}
      {!isLinked && hasParent && (
        <Folder className="w-3 h-3 text-[#71717a] shrink-0" />
      )}
      {!isLinked && hasDescription && (
        <Info className="w-3 h-3 text-[#71717a] shrink-0" />
      )}
      <span className="group-hover:pr-5 transition-all">{name}</span>
      {onEdit && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="w-3 h-3 text-[#71717a] group-hover:text-[#18181b] transition-colors" />
        </span>
      )}
      {(hasDescription ||
        (hasParent && parentName) ||
        (isLinked && extractedName)) &&
        showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#27272a] text-white text-xs rounded shadow-lg z-10 max-w-xs whitespace-normal pointer-events-none">
            {hasParent && parentName && (
              <div className="font-semibold">{t("documentReview.modal.group")}: {parentName}</div>
            )}
            {isLinked && extractedName && (
              <div className="font-semibold">
                {t("documentReview.modal.extractedName")}: {extractedName}
              </div>
            )}
            {hasDescription && <div>{description}</div>}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-[#27272a]"></div>
          </div>
        )}
    </span>
  );
}
